// ============================================================================
// VoiceRoomMemberRow.tsx — строка участника голосовой комнаты: точка «говорит»,
// роль (подпись или селект для модератора), состояние микрофона и кнопка
// мьюта (модератор — с подтверждением на уровне родителя, VoiceRoomView).
// ============================================================================

import { Crown, Mic, MicOff } from 'lucide-react';
import type { VoiceRoomMember } from '@zdc/sdk';
import { Button } from '@/components/ui/button';
import { VOICE_ROLE_LABELS, memberLabel } from './prefs';

export interface VoiceRoomMemberRowProps {
  m: VoiceRoomMember;
  myId: string;
  isCreator: boolean;
  speaking: boolean;
  /** Модератор/создатель/админ может менять роль и мьютить этого участника. */
  canAct: boolean;
  busy: boolean;
  onRoleChange: (m: VoiceRoomMember, role: string) => void;
  onToggleMute: (m: VoiceRoomMember) => void;
}

export function VoiceRoomMemberRow({
  m,
  myId,
  isCreator,
  speaking,
  canAct,
  busy,
  onRoleChange,
  onToggleMute,
}: VoiceRoomMemberRowProps) {
  const active = m.state === 'active';
  const muted = m.mutedSelf || m.mutedBy;
  return (
    <li
      className={`flex items-center gap-2 rounded-md border px-2 py-1.5 ${active ? '' : 'opacity-60'}`}
    >
      {isCreator ? (
        <Crown className="h-3.5 w-3.5 text-amber-500 shrink-0" />
      ) : (
        <span
          className={`h-2 w-2 rounded-full shrink-0 ${speaking ? 'bg-green-500' : 'bg-muted-foreground/40'}`}
        />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{memberLabel(m, myId)}</div>
        <div className="text-xs text-muted-foreground">
          {VOICE_ROLE_LABELS[m.role] ?? m.role}
          {!active && ' · вышел'}
        </div>
      </div>
      {muted && (
        <span
          title={m.mutedBy ? 'Микрофон выключен ведущим' : 'Микрофон выключен'}
          className={m.mutedBy ? 'text-destructive' : 'text-muted-foreground'}
        >
          <MicOff className="h-4 w-4" />
        </span>
      )}
      {canAct && (
        <>
          <select
            value={m.role}
            onChange={(e) => onRoleChange(m, e.target.value)}
            disabled={busy}
            className="h-7 rounded-md border bg-background px-1 text-xs"
            title="Роль участника"
          >
            <option value="moderator">Ведущий</option>
            <option value="speaker">Говорящий</option>
            <option value="listener">Слушающий</option>
          </select>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            onClick={() => onToggleMute(m)}
            disabled={busy}
            title={m.mutedBy ? 'Включить микрофон' : 'Выключить микрофон'}
          >
            {m.mutedBy ? <Mic className="h-3.5 w-3.5" /> : <MicOff className="h-3.5 w-3.5" />}
          </Button>
        </>
      )}
    </li>
  );
}
